import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  EMAIL_KIND_CORRECTIONS_APPLIED,
  EMAIL_KIND_PROJECT_REVIEWED,
  EMAIL_KIND_MOUNTING_COMPLETE,
  EMAIL_KIND_PRE_ARCHIVE_UPLOAD,
  EMAIL_KIND_PRODUCTION_HANDOFF,
  EMAIL_KIND_REVIEW_CHECK,
  type EmailKind,
  type Project,
  type ProjectFile,
  type EmailSendBody,
  type ReviewCheckModalProjectPayload,
  type PreArchiveUploadModalProjectPayload,
  type MountingCompleteModalProjectPayload,
  type CorrectionsAppliedModalProjectPayload,
  type ProjectReviewedModalProjectPayload,
} from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import type { RecipientUserItem } from '@/components/UserRecipientCombo/UserRecipientCombo';
import type { ContractorValueItem } from '@/components/ContractorCombo/ContractorCombo';
import type { DepartmentValueItem } from '@/components/DepartmentCombo/DepartmentCombo';
import type { EditionValueItem } from '@/components/EditionCombo/EditionCombo';
import type { EditorValueItem } from '@/components/EditorCombo/EditorCombo';
import { useEditionEditorDepartmentHandlers } from '@/hooks/useEditionEditorDepartmentHandlers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useEmailDraft, type EmailDraftRequest } from '@/hooks/useEmailDraft';
import { useEmailSend } from '@/hooks/useEmailSend';
import { useEmailAttachments } from '@/hooks/useEmailAttachments';
import { useProjectEntity } from '@/hooks/useProjectEntity';
import { getFirstStudioContractorFromProject } from '@/utils/projectStudios';
import {
  projectReviewDeadlineToDisplay,
  reviewDeadlineDisplayToApi,
} from '@/utils/emailDateUtils';
import { EmailModalShell } from './EmailModalShell';
import { EmailPreviewModal } from './EmailPreviewModal';
import { ReviewCheckEmailFields } from './ReviewCheckEmailFields';
import { ProductionHandoffEmailFields } from './ProductionHandoffEmailFields';
import { useProductionHandoffDeadline } from './useProductionHandoffDeadline';
import { buildProductionHandoffModalPayload } from './productionHandoffPayload';
import { buildCorrectionsAppliedModalPayload, correctionsReviewDeadlineToDisplay } from './correctionsAppliedPayload';
import { PreArchiveUploadEmailFields } from './PreArchiveUploadEmailFields';
import { MountingCompleteEmailFields } from './MountingCompleteEmailFields';
import { CorrectionsAppliedEmailFields } from './CorrectionsAppliedEmailFields';
import { ProjectReviewedEmailFields } from './ProjectReviewedEmailFields';
import type { TecheditorValueItem } from '@/components/TecheditorCombo/TecheditorCombo';
import { Button } from '@/components/ui/Button/Button';
import { ProjectFilesPopover } from '@/components/ProjectFilesPopover/ProjectFilesPopover';
import { getEmailAttachFilesButtonLabel } from '@/utils/emailAttachFilesLabel';
import { projectProducersToRecipients } from '@/utils/projectEmailRecipients';
import {
  DEFAULT_PRODUCTION_HANDOFF_INTRO_PHRASE,
  type ProductionHandoffIntroPhrase,
} from '@/constants/productionHandoffIntroPhrases';
import './ReviewCheckEmailModal.css';

export interface ProjectEmailModalProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  emailKind: EmailKind;
  modalTitle: string;
  requireNewState?: boolean;
  newStateWhenSaving?: string;
}

const MISSING_EMAIL_MSG = (names: string[]) =>
  `Нет email в профиле: ${names.join(', ')} — письмо отправить нельзя, пока не добавите email этим пользователям.`;

export function ProjectEmailModal({
  open,
  onClose,
  project,
  emailKind,
  modalTitle,
  requireNewState = false,
  newStateWhenSaving,
}: ProjectEmailModalProps) {
  const isProductionForm = emailKind === EMAIL_KIND_PRODUCTION_HANDOFF;
  const isPreArchiveForm = emailKind === EMAIL_KIND_PRE_ARCHIVE_UPLOAD;
  const isMountingForm = emailKind === EMAIL_KIND_MOUNTING_COMPLETE;
  const isCorrectionsAppliedForm = emailKind === EMAIL_KIND_CORRECTIONS_APPLIED;
  const isProjectReviewedForm = emailKind === EMAIL_KIND_PROJECT_REVIEWED;
  const { user } = useAuth();
  const { showError } = useToast();

  // ── Form state ───────────────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<RecipientUserItem[]>([]);
  const [ccUsers, setCcUsers] = useState<RecipientUserItem[]>([]);
  const [reviewType, setReviewType] = useState('');
  const [reviewDeadline, setReviewDeadline] = useState('');
  /** «Правки внесены»: срок «Завершить проверку правок до», дд.мм.гггг */
  const [correctionsReviewDeadline, setCorrectionsReviewDeadline] = useState('');
  const [studioContractor, setStudioContractor] = useState<ContractorValueItem | null>(null);
  const [asta, setAsta] = useState('');
  const [tracksCount, setTracksCount] = useState('');
  const [sizeValue, setSizeValue] = useState('');
  const [time, setTime] = useState('');
  const [departmentValue, setDepartmentValue] = useState<DepartmentValueItem | null>(null);
  const [editionValue, setEditionValue] = useState<EditionValueItem | null>(null);
  const [editorValue, setEditorValue] = useState<EditorValueItem | null>(null);
  const [commentNote, setCommentNote] = useState('');
  const [correctionsCount, setCorrectionsCount] = useState('');
  const [techeditors, setTecheditors] = useState<TecheditorValueItem[]>([]);
  const [introPhrase, setIntroPhrase] = useState<ProductionHandoffIntroPhrase>(
    DEFAULT_PRODUCTION_HANDOFF_INTRO_PHRASE
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [preArchiveNoEdits, setPreArchiveNoEdits] = useState(false);
  const [reviewNoEdits, setReviewNoEdits] = useState(false);
  const attachAnchorRef = useRef<HTMLDivElement>(null);
  /** Актуальная сущность из ['project', id]; проп project может отставать от кэша списков. */
  const liveProject = useProjectEntity(project.id, project) ?? project;
  const emailModalJustOpenedRef = useRef(false);
  const handoffDeadline = useProductionHandoffDeadline(liveProject);
  const { deadlineField, deadlineDate, reset: resetHandoffDeadline } = handoffDeadline;

  const { onDepartmentChange, onEditionChange } = useEditionEditorDepartmentHandlers(
    setDepartmentValue,
    setEditionValue,
    setEditorValue
  );

  // ── Attachments ──────────────────────────────────────────────────────────────
  const emailAttachmentPreselectTypes = useMemo(():
    | readonly ['Правки']
    | readonly ['Сэмпл']
    | readonly ['Текст']
    | undefined => {
    if (isCorrectionsAppliedForm || isProjectReviewedForm) return ['Правки'] as const;
    if (isPreArchiveForm) return ['Сэмпл'] as const;
    if (emailKind === EMAIL_KIND_REVIEW_CHECK) return ['Текст'] as const;
    return undefined;
  }, [isCorrectionsAppliedForm, isProjectReviewedForm, isPreArchiveForm, emailKind]);

  const {
    emailAttachmentFileIds,
    emailAttachmentIdsSorted,
    emailFilesPopoverOpen,
    setEmailFilesPopoverOpen,
    handleToggleEmailAttachment,
  } = useEmailAttachments({
    open,
    projectId: project.id,
    projectFiles: (liveProject.files ?? []) as ProjectFile[],
    preselectFileTypes: emailAttachmentPreselectTypes,
  });

  const { data: ccPersonFromDb } = useQuery({
    queryKey: ['users', user?.id],
    queryFn: () => api.users.get(user!.id),
    enabled: open && Boolean(user?.id),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) {
      setCcUsers([]);
      return;
    }
    if (!ccPersonFromDb) return;
    const displayName =
      ccPersonFromDb.name?.trim() ||
      `${ccPersonFromDb.first_name} ${ccPersonFromDb.last_name}`.trim();
    setCcUsers([{
      id: ccPersonFromDb.id,
      name: displayName,
      email: ccPersonFromDb.email ?? null,
    }]);
  }, [open, ccPersonFromDb]);

  // ── Initialization ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      emailModalJustOpenedRef.current = false;
      setPreviewModalOpen(false);
      return;
    }
    const justOpened = !emailModalJustOpenedRef.current;
    emailModalJustOpenedRef.current = true;
    if (!justOpened) return;
    if (isProductionForm) {
      setRecipients([]);
      resetHandoffDeadline();
      setCommentNote('');
      setIntroPhrase(DEFAULT_PRODUCTION_HANDOFF_INTRO_PHRASE);
      return;
    }
    if (isPreArchiveForm) {
      setRecipients(projectProducersToRecipients(liveProject.producers));
      setTime(liveProject.time ?? '');
      setTracksCount(liveProject.tracks_count != null ? String(liveProject.tracks_count) : '');
      setSizeValue(liveProject.size != null ? String(liveProject.size) : '');
      setPreArchiveNoEdits(!!liveProject.no_edits);
      setCommentNote('');
      return;
    }
    if (isMountingForm) {
      setRecipients([]);
      setTime(liveProject.time ?? '');
      setTracksCount(liveProject.tracks_count != null ? String(liveProject.tracks_count) : '');
      setSizeValue(liveProject.size != null ? String(liveProject.size) : '');
      setCommentNote('');
      return;
    }
    if (isCorrectionsAppliedForm) {
      const te = liveProject.techeditors ?? [];
      setRecipients(
        te.map((t) => ({
          id: t.id,
          name: t.name,
          email: t.email ?? null,
        }))
      );
      setCorrectionsReviewDeadline(correctionsReviewDeadlineToDisplay(liveProject));
      setCommentNote('');
      return;
    }
    if (isProjectReviewedForm) {
      setRecipients(projectProducersToRecipients(liveProject.producers));
      setTecheditors(
        (liveProject.techeditors ?? []).map((t) => ({
          id: t.id,
          name: t.name,
          email: t.email ?? null,
        }))
      );
      setReviewType(
        liveProject.review_check_type != null && String(liveProject.review_check_type).trim()
          ? String(liveProject.review_check_type)
          : ''
      );
      setCorrectionsCount(
        liveProject.corrections_number != null ? String(liveProject.corrections_number) : ''
      );
      setReviewNoEdits(liveProject.no_edits === true);
      setCommentNote('');
      return;
    }
    setRecipients([]);
    setReviewType(
      liveProject.review_check_type != null && String(liveProject.review_check_type).trim()
        ? String(liveProject.review_check_type)
        : ''
    );
    setReviewDeadline(projectReviewDeadlineToDisplay(liveProject.review_deadline));
    setAsta(liveProject.asta ?? '');
    setTracksCount(liveProject.tracks_count != null ? String(liveProject.tracks_count) : '');
    setSizeValue(liveProject.size != null ? String(liveProject.size) : '');
    setTime(liveProject.time ?? '');
    setDepartmentValue(
      liveProject.edition_department_id
        ? { id: liveProject.edition_department_id, code: liveProject.edition_department_code ?? '' }
        : null
    );
    setEditionValue(
      liveProject.edition_id
        ? {
            id: liveProject.edition_id,
            name: liveProject.edition_name ?? '',
            department_id: liveProject.edition_department_id ?? undefined,
            department_code: liveProject.edition_department_code ?? undefined,
          }
        : null
    );
    setEditorValue(
      liveProject.editor_id
        ? {
            id: liveProject.editor_id,
            name: liveProject.editor_name ?? '',
            edition_id: liveProject.edition_id ?? undefined,
            edition_name: liveProject.edition_name ?? undefined,
            department_id: liveProject.edition_department_id ?? undefined,
            department_code: liveProject.edition_department_code ?? undefined,
          }
        : null
    );
    setCommentNote('');
    setStudioContractor(getFirstStudioContractorFromProject(liveProject));
  }, [
    open,
    liveProject,
    resetHandoffDeadline,
    isProductionForm,
    isPreArchiveForm,
    isMountingForm,
    isCorrectionsAppliedForm,
    isProjectReviewedForm,
  ]);

  // ── Payload computation ──────────────────────────────────────────────────────
  const modalProjectPayloadBase = useMemo(
    ():
      | Omit<ReviewCheckModalProjectPayload, 'email_attachment_file_ids'>
      | PreArchiveUploadModalProjectPayload
      | MountingCompleteModalProjectPayload
      | CorrectionsAppliedModalProjectPayload
      | ProjectReviewedModalProjectPayload => {
      if (isProductionForm) {
        return buildProductionHandoffModalPayload({ note: commentNote, introPhrase, deadlineField, deadlineDate });
      }
      if (isPreArchiveForm) {
        const tracksTrim = tracksCount.trim();
        const tracksParsed =
          tracksTrim !== ''
            ? Number.isFinite(parseInt(tracksTrim, 10))
              ? Math.max(0, parseInt(tracksTrim, 10))
              : null
            : null;
        const sizeTrim = sizeValue.trim();
        const sizeParsed =
          sizeTrim !== ''
            ? Number.isFinite(parseFloat(sizeTrim.replace(',', '.')))
              ? parseFloat(sizeTrim.replace(',', '.'))
              : null
            : null;
        return {
          time: time.trim() || null,
          tracks_count: tracksParsed,
          size: sizeParsed,
          no_edits: preArchiveNoEdits,
          note: commentNote.trim() || null,
        };
      }
      if (isMountingForm) {
        const tracksTrim = tracksCount.trim();
        const tracksParsed =
          tracksTrim !== ''
            ? Number.isFinite(parseInt(tracksTrim, 10))
              ? Math.max(0, parseInt(tracksTrim, 10))
              : null
            : null;
        const sizeTrim = sizeValue.trim();
        const sizeParsed =
          sizeTrim !== ''
            ? Number.isFinite(parseFloat(sizeTrim.replace(',', '.')))
              ? parseFloat(sizeTrim.replace(',', '.'))
              : null
            : null;
        return {
          time: time.trim() || null,
          tracks_count: tracksParsed,
          size: sizeParsed,
          note: commentNote.trim() || null,
        };
      }
      if (isCorrectionsAppliedForm) {
        return buildCorrectionsAppliedModalPayload({ note: commentNote, reviewDeadline: correctionsReviewDeadline });
      }
      if (isProjectReviewedForm) {
        const correctionsTrim = correctionsCount.trim();
        const correctionsParsed = reviewNoEdits
          ? null
          : correctionsTrim !== ''
            ? Number.isFinite(parseInt(correctionsTrim, 10))
              ? Math.max(0, parseInt(correctionsTrim, 10))
              : null
            : null;
        return {
          techeditor_person_ids: [...techeditors].map((t) => t.id).sort(),
          review_check_type: reviewType === '' ? null : reviewType,
          corrections_number: correctionsParsed,
          no_edits: reviewNoEdits,
          note: commentNote.trim() || null,
        };
      }
      const tracksTrim = tracksCount.trim();
      const tracksParsed =
        tracksTrim !== '' ? (Number.isFinite(parseInt(tracksTrim, 10)) ? Math.max(0, parseInt(tracksTrim, 10)) : null) : null;
      const sizeTrim = sizeValue.trim();
      const sizeParsed =
        sizeTrim !== '' ? (Number.isFinite(parseFloat(sizeTrim.replace(',', '.'))) ? parseFloat(sizeTrim.replace(',', '.')) : null) : null;
      return {
        studio_contractor_id: studioContractor?.id ?? null,
        asta: asta.trim() || null,
        edition_id: editionValue?.id ?? null,
        editor_id: editorValue?.id ?? null,
        edition_department_code: departmentValue?.code?.trim() || null,
        edition_name: editionValue?.name?.trim() || null,
        time: time.trim() || null,
        tracks_count: tracksParsed,
        size: sizeParsed,
        note: commentNote.trim() || null,
      };
    },
    [
      isProductionForm,
      isPreArchiveForm,
      isMountingForm,
      isCorrectionsAppliedForm,
      isProjectReviewedForm,
      studioContractor,
      commentNote,
      correctionsReviewDeadline,
      correctionsCount,
      techeditors,
      reviewType,
      introPhrase,
      deadlineField,
      deadlineDate,
      asta,
      editionValue,
      editorValue,
      departmentValue,
      time,
      tracksCount,
      sizeValue,
      preArchiveNoEdits,
      reviewNoEdits,
    ]
  );

  const modalProjectPayload:
    | ReviewCheckModalProjectPayload
    | PreArchiveUploadModalProjectPayload
    | MountingCompleteModalProjectPayload
    | CorrectionsAppliedModalProjectPayload
    | ProjectReviewedModalProjectPayload = useMemo(
    () => ({ ...modalProjectPayloadBase, email_attachment_file_ids: emailAttachmentIdsSorted }),
    [modalProjectPayloadBase, emailAttachmentIdsSorted]
  );

  // ── Debounced draft inputs ───────────────────────────────────────────────────
  const recipientIdsSorted = useMemo(
    () => [...recipients].map((t) => t.id).sort().join(','),
    [recipients]
  );
  const flushKey = `${open}-${project.id}-${emailKind}`;
  const debouncedPayloadBase = useDebouncedValue(modalProjectPayloadBase, 1000, flushKey);
  const debouncedReviewDeadline = useDebouncedValue(reviewDeadline, 1000, flushKey);

  const draftModalProjectPayload:
    | ReviewCheckModalProjectPayload
    | PreArchiveUploadModalProjectPayload
    | MountingCompleteModalProjectPayload
    | CorrectionsAppliedModalProjectPayload
    | ProjectReviewedModalProjectPayload = useMemo(
    () => ({ ...debouncedPayloadBase, email_attachment_file_ids: emailAttachmentIdsSorted }),
    [debouncedPayloadBase, emailAttachmentIdsSorted]
  );

  const draftDepsKey = useMemo(() => {
    if (isProductionForm) {
      return `${emailKind}|${project.id}|${recipientIdsSorted}|${JSON.stringify(draftModalProjectPayload)}`;
    }
    if (isPreArchiveForm || isMountingForm || isCorrectionsAppliedForm || isProjectReviewedForm) {
      return `${emailKind}|${project.id}|${recipientIdsSorted}|${JSON.stringify(draftModalProjectPayload)}`;
    }
    return `${emailKind}|${project.id}|${recipientIdsSorted}|${reviewType}|${debouncedReviewDeadline}|${JSON.stringify(draftModalProjectPayload)}`;
  }, [
    isProductionForm,
    isPreArchiveForm,
    isMountingForm,
    isCorrectionsAppliedForm,
    isProjectReviewedForm,
    emailKind,
    project.id,
    recipientIdsSorted,
    reviewType,
    debouncedReviewDeadline,
    draftModalProjectPayload,
  ]);

  const draftRequest = useMemo(
    (): EmailDraftRequest => ({
      kind: emailKind,
      project_id: project.id,
      recipient_person_ids: recipientIdsSorted ? recipientIdsSorted.split(',').filter(Boolean) : [],
      ...(isProductionForm || isPreArchiveForm || isMountingForm || isCorrectionsAppliedForm || isProjectReviewedForm
        ? {}
        : {
            review_check_type: reviewType === '' ? null : reviewType,
            review_deadline: reviewDeadlineDisplayToApi(debouncedReviewDeadline),
          }),
      modal_project: draftModalProjectPayload,
    }),
    [
      emailKind,
      project.id,
      recipientIdsSorted,
      isProductionForm,
      isPreArchiveForm,
      isMountingForm,
      isCorrectionsAppliedForm,
      isProjectReviewedForm,
      reviewType,
      debouncedReviewDeadline,
      draftModalProjectPayload,
    ]
  );

  // ── Draft hook ───────────────────────────────────────────────────────────────
  const { previewHtml, draftLoading } = useEmailDraft({
    open,
    draftDepsKey,
    request: draftRequest,
    onError: showError,
  });

  // ── Send hook ────────────────────────────────────────────────────────────────
  const { mutate: sendMutate, isPending: isSending, variables: sendVars } = useEmailSend({
    onClose,
  });

  // ── Missing email validation toast ──────────────────────────────────────────
  const lastMissingToastKeyRef = useRef('');

  const missingEmailNames = useMemo(() => {
    const names = recipients.filter((t) => !t.email?.trim()).map((t) => t.name);
    for (const cc of ccUsers) {
      if (!cc.email?.trim()) names.push(cc.name);
    }
    return names;
  }, [recipients, ccUsers]);

  const missingEmailToastKey = useMemo(() => {
    const parts = recipients.filter((t) => !t.email?.trim()).map((t) => `to:${t.id}`);
    for (const cc of ccUsers) {
      if (!cc.email?.trim()) parts.push(`cc:${cc.id}`);
    }
    return parts.sort().join('\u0001');
  }, [recipients, ccUsers]);

  useEffect(() => {
    if (!open) { lastMissingToastKeyRef.current = ''; return; }
    if (!missingEmailToastKey) { lastMissingToastKeyRef.current = ''; return; }
    if (missingEmailToastKey === lastMissingToastKeyRef.current) return;
    lastMissingToastKeyRef.current = missingEmailToastKey;
    showError(MISSING_EMAIL_MSG(missingEmailNames));
  }, [open, missingEmailToastKey, missingEmailNames, showError]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const buildSendPayload = useCallback(
    (skip_send: boolean): EmailSendBody => {
      const base: EmailSendBody = {
        kind: emailKind,
        project_id: project.id,
        skip_send,
        new_state: newStateWhenSaving,
        recipient_person_ids: recipients.map((t) => t.id),
        cc_person_ids: ccUsers.map((t) => t.id),
        modal_project: modalProjectPayload,
        changed_by_user_id: user?.id,
      };
      if (isProductionForm || isPreArchiveForm || isMountingForm || isCorrectionsAppliedForm || isProjectReviewedForm) return base;
      return {
        ...base,
        review_check_type: reviewType === '' ? null : reviewType,
        review_deadline: reviewDeadlineDisplayToApi(reviewDeadline),
      };
    },
    [
      emailKind,
      isProductionForm,
      isPreArchiveForm,
      isMountingForm,
      isCorrectionsAppliedForm,
      isProjectReviewedForm,
      newStateWhenSaving,
      recipients,
      ccUsers,
      user?.id,
      project.id,
      reviewType,
      reviewDeadline,
      modalProjectPayload,
    ]
  );

  const targetStateMissing = requireNewState && !newStateWhenSaving?.trim();

  const handleSaveWithoutEmail = useCallback(() => {
    if (targetStateMissing) { showError('Не указан целевой статус'); return; }
    sendMutate(buildSendPayload(true));
  }, [targetStateMissing, sendMutate, buildSendPayload, showError]);

  const handleSaveWithEmail = useCallback(() => {
    if (targetStateMissing) { showError('Не указан целевой статус'); return; }
    if (recipients.length === 0) { showError('Выберите получателей письма'); return; }
    if (missingEmailNames.length > 0) { showError(MISSING_EMAIL_MSG(missingEmailNames)); return; }
    sendMutate(buildSendPayload(false));
  }, [
    targetStateMissing,
    recipients.length,
    missingEmailNames,
    sendMutate,
    buildSendPayload,
    showError,
  ]);

  const submittingWithoutEmail = isSending && sendVars?.skip_send === true;
  const submittingWithEmail = isSending && sendVars?.skip_send === false;

  // ── Render ───────────────────────────────────────────────────────────────────
  const fieldsSlot = isProductionForm ? (
    <ProductionHandoffEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      deadlineField={deadlineField}
      onDeadlineFieldChange={handoffDeadline.selectField}
      deadlineDate={deadlineDate}
      onDeadlineDateChange={handoffDeadline.setDeadlineDate}
      introPhrase={introPhrase}
      onIntroPhraseChange={setIntroPhrase}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      disabled={isSending}
    />
  ) : isProjectReviewedForm ? (
    <ProjectReviewedEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      techeditors={techeditors}
      onTecheditorsChange={setTecheditors}
      reviewType={reviewType}
      onReviewTypeChange={setReviewType}
      correctionsCount={correctionsCount}
      onCorrectionsCountChange={setCorrectionsCount}
      noEdits={reviewNoEdits}
      onNoEditsChange={(next) => {
        setReviewNoEdits(next);
        if (next) setCorrectionsCount('');
      }}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      disabled={isSending}
    />
  ) : isCorrectionsAppliedForm ? (
    <CorrectionsAppliedEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      reviewDeadline={correctionsReviewDeadline}
      onReviewDeadlineChange={setCorrectionsReviewDeadline}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      disabled={isSending}
    />
  ) : isMountingForm ? (
    <MountingCompleteEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      time={time}
      onTimeChange={setTime}
      filesCount={tracksCount}
      onFilesCountChange={setTracksCount}
      sizeValue={sizeValue}
      onSizeValueChange={setSizeValue}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      disabled={isSending}
    />
  ) : isPreArchiveForm ? (
    <PreArchiveUploadEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      time={time}
      onTimeChange={setTime}
      tracksCount={tracksCount}
      onTracksCountChange={setTracksCount}
      sizeValue={sizeValue}
      onSizeValueChange={setSizeValue}
      noEdits={preArchiveNoEdits}
      onNoEditsChange={setPreArchiveNoEdits}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      disabled={isSending}
    />
  ) : (
    <ReviewCheckEmailFields
      recipients={recipients}
      onRecipientsChange={setRecipients}
      ccUsers={ccUsers}
      onCcUsersChange={setCcUsers}
      reviewType={reviewType}
      onReviewTypeChange={setReviewType}
      reviewDeadline={reviewDeadline}
      onReviewDeadlineChange={setReviewDeadline}
      studioContractor={studioContractor}
      onStudioContractorChange={setStudioContractor}
      asta={asta}
      onAstaChange={setAsta}
      departmentValue={departmentValue}
      onDepartmentChange={onDepartmentChange}
      editionValue={editionValue}
      onEditionChange={onEditionChange}
      time={time}
      onTimeChange={setTime}
      tracksCount={tracksCount}
      onTracksCountChange={setTracksCount}
      sizeValue={sizeValue}
      onSizeValueChange={setSizeValue}
      commentNote={commentNote}
      onCommentNoteChange={setCommentNote}
      onCreateError={(e) => showError(e.message)}
      disabled={isSending}
    />
  );

  return (
    <>
      <EmailModalShell
        open={open}
        onClose={onClose}
        title={modalTitle}
        fieldsSlot={fieldsSlot}
        attachFilesSlot={
          <div ref={attachAnchorRef} className="review-check-email-modal__attach-wrap">
            <Button
              fullWidth
              variant="outlined"
              type="button"
              onClick={() => setEmailFilesPopoverOpen(!emailFilesPopoverOpen)}
              disabled={isSending}
            >
              {getEmailAttachFilesButtonLabel(emailAttachmentIdsSorted.length)}
            </Button>
          </div>
        }
        onOpenPreview={() => setPreviewModalOpen(true)}
        previewTriggerDisabled={isSending}
        onSaveWithoutEmail={handleSaveWithoutEmail}
        saveWithoutEmailDisabled={draftLoading || targetStateMissing}
        onSaveWithEmail={handleSaveWithEmail}
        saveWithEmailDisabled={
          draftLoading || !previewHtml.trim() || recipients.length === 0 || missingEmailNames.length > 0
        }
        isSubmitting={isSending}
        submittingWithoutEmail={submittingWithoutEmail}
        submittingWithEmail={submittingWithEmail}
      />
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        html={previewHtml}
        loading={draftLoading}
      />
      <ProjectFilesPopover
        open={emailFilesPopoverOpen}
        onClose={() => setEmailFilesPopoverOpen(false)}
        project={liveProject}
        anchorEl={attachAnchorRef.current}
        fileLinkSelection={{
          selectedIds: emailAttachmentFileIds,
          onToggle: handleToggleEmailAttachment,
        }}
      />
    </>
  );
}

// Re-export date utilities for backward compatibility
export { projectReviewDeadlineToDisplay, reviewDeadlineDisplayToApi } from '@/utils/emailDateUtils';
